/**
 * Where an aircraft is going next, for the scope's route-ahead line: the rest of its route, a
 * direct-to fix and the filed plan after it, its holding fix, a short stub along its heading, and
 * the published approach fixes still ahead and the landing threshold once it has an approach. Pure; reads the live aircraft.
 */
import { destination, HEADING_STUB_NM, NM_TO_M, type AirportData, type LatLon, type RouteLeg } from '@atc/core';
import type { Aircraft } from './aircraft';

export interface PathPoint extends LatLon {
  /** Fix or runway name; unlabelled points are heading stubs and the aircraft itself. */
  label?: string;
}

/** Fix legs in order; a heading leg ends the known path with a stub (radar vectors follow). */
function appendLegs(airport: AirportData, legs: readonly RouteLeg[], from: LatLon, out: PathPoint[]): void {
  let cur = from;
  for (const leg of legs) {
    if (leg.kind === 'fix') {
      const f = airport.fixes.get(leg.fix);
      if (!f) continue;
      out.push({ lat: f.lat, lon: f.lon, label: leg.fix });
      cur = f;
    } else {
      out.push(destination(cur.lat, cur.lon, leg.headingDeg, HEADING_STUB_NM * NM_TO_M));
      return;
    }
  }
}

export function plannedPath(ac: Aircraft, airport: AirportData): PathPoint[] {
  const out: PathPoint[] = [{ lat: ac.lat, lon: ac.lon }];
  if (ac.onGround) return out;
  const nav = ac.nav;
  const thr = ac.approach?.runway;
  if (thr && (nav.mode === 'localizer' || ac.approach!.established)) {
    if (!ac.approach!.established) appendLegs(airport, (ac.approach!.via ?? []).map((v) => ({ kind: 'fix', fix: v.fix })), ac, out);
    out.push({ lat: thr.lat, lon: thr.lon, label: thr.name });
    return out;
  }
  switch (nav.mode) {
    case 'route':
      appendLegs(airport, (nav.route ?? []).slice(nav.routeIdx), ac, out);
      break;
    case 'direct': {
      const f = nav.fix ? airport.fixes.get(nav.fix) : undefined;
      if (!f) break;
      out.push({ lat: f.lat, lon: f.lon, label: f.name });
      const i = ac.flightPlan.findIndex((l) => l.kind === 'fix' && l.fix === f.name);
      if (i >= 0) appendLegs(airport, ac.flightPlan.slice(i + 1), f, out);
      break;
    }
    case 'hold': {
      const name = nav.hold?.fix ?? nav.fix;
      const f = name ? airport.fixes.get(name) : undefined;
      if (f) out.push({ lat: f.lat, lon: f.lon, label: f.name });
      break;
    }
    default:
      out.push(destination(ac.lat, ac.lon, nav.headingDeg, HEADING_STUB_NM * NM_TO_M));
  }
  // Cleared for an approach but not yet on the localizer: the intercept ends at the runway.
  if (thr) out.push({ lat: thr.lat, lon: thr.lon, label: thr.name });
  return out;
}
